import { useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import useUIStore from '../../store/uiStore';
import { getDisplayTitle } from '../../utils/getDisplayTitle';
import { PageThumbnail } from '../../components/ui/PageThumbnail';





export default function RightSidebar({ collapsed, document }) {
  const { toggleRightSidebar } = useUIStore();
  const [activeTab, setActiveTab] = useState('info');
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  const searchParams = new URLSearchParams(location.search);
  const isCropMode = searchParams.get('mode') === 'crop';

  if (collapsed) {
    return (
      <aside className="w-11 h-screen bg-theme-secondary border-l border-theme-primary flex flex-col items-center py-4 transition-colors duration-300">
        <button
          onClick={toggleRightSidebar}
          className="p-2 rounded-xl hover:bg-theme-tertiary text-theme-muted hover:text-theme-primary transition-colors"
          title="Expand panel"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
      </aside>
    );
  }

  const getStatusInfo = () => {
    if (!document) return { label: 'No document', color: 'text-theme-muted', bg: 'bg-theme-elevated' };
    if (document.is_processed) return { label: 'Processed', color: 'text-status-success', bg: 'bg-status-success/10' };
    if (document.has_error) return { label: 'Failed', color: 'text-status-error', bg: 'bg-status-error/10' };
    return { label: 'Pending', color: 'text-theme-muted', bg: 'bg-theme-elevated' };
  };

  const status = getStatusInfo();

  return (
    <aside className="w-[259px] h-screen bg-theme-secondary border-l border-theme-primary flex flex-col transition-colors duration-300">
      {/* Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-theme-primary flex-shrink-0">
        <h2 className="text-sm font-semibold text-theme-primary">
          Document Pages
        </h2>
        <div className="flex items-center gap-2">
          {document?.pages && (
            <span className="text-xs bg-theme-elevated px-2 py-0.5 rounded-full text-brand-amber border border-theme-primary/50">
              {document.pages.length}
            </span>
          )}
          <button
            onClick={toggleRightSidebar}
            className="p-2 rounded-xl hover:bg-theme-tertiary text-theme-muted hover:text-theme-primary transition-colors"
            title="Close panel"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Pages List — always visible */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 custom-scrollbar">
        {!document?.pages || document.pages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-theme-muted text-sm">Loading...</div>
        ) : (
          document.pages.map((p) => {
            const isCurrent = p.id.toString() === id?.toString();
            const modeParam = isCropMode ? '?mode=crop' : '';
            return (
              <div
                key={p.id}
                onClick={() => {
                  if (!isCurrent) {
                    navigate(`/pages/${p.id}/edit${modeParam}`);
                  }
                }}
                className={`relative aspect-[1/1.4] rounded-lg border-2 overflow-hidden transition-all ${isCurrent
                    ? 'border-brand-orange shadow-md shadow-brand-orange/20 scale-[1.02] z-10'
                    : 'border-theme-primary hover:border-brand-amber/50 hover:opacity-100 opacity-60 cursor-pointer'
                  }`}
              >
                <div className="w-full h-full bg-theme-surface">
                  <PageThumbnail src={p.image} alt={`Page ${p.page_number}`} className="w-full h-full object-cover" />
                </div>
                <div className={`absolute bottom-0 left-0 right-0 py-1.5 px-2 text-[10px] sm:text-xs font-medium backdrop-blur-md flex items-center justify-between ${isCurrent ? 'bg-brand-orange/90 text-white' : 'bg-black/70 text-white/90'
                  }`}>
                  <span>Page {p.page_number}</span>
                  {isCurrent && <span className="w-1.5 h-1.5 rounded-full bg-white ml-1 shadow-sm"></span>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
